<template>
	<label
		class="pronto-checkbox"
		:for="id"
		:class="{ pronto, loading, checked: model, unchecked: !model, disabled }"
	>
		<input
			:id="id"
			class="input"
			type="checkbox"
			v-model="model"
			:disabled="disabled"
			@focus="focused = true"
			@blur="focused = false"
			@keyup.space="toggleValue"
			@keyup.enter="$emit('enter', $event)"
		/>
		<div class="pronto-checkbox-icon">
			<IosSpinner v-if="loading" />
			<SquareIcon type="regular" class="empty" />
			<SquareCheckIcon type="solid" class="filled" />
		</div>
		<slot />
	</label>
</template>

<script setup>
import { ref, getCurrentInstance } from 'vue'
import IosSpinner from '../IosSpinner'
import { SquareIcon, SquareCheckIcon } from '@/icons'
import { watch } from 'vue'

defineOptions({ name: 'Checkbox' })
const props = defineProps({
	value: { type: Boolean, default: null },
	disabled: { type: Boolean, default: null },
	pronto: { type: Boolean, default: null },
	loading: { type: Boolean, default: null },
})
const emit = defineEmits(['change'])

const instance = getCurrentInstance()
const model = defineModel()
if (model.value == null && props.value != null) {
	// handle cases without v-model where the value prop is set
	model.value = props.value
}

function toggleValue() {
	model.value = !model.value
}

const focused = ref(false)
const id = instance.uid

watch(model, () => emit('change', model.value))
watch(
	() => props.value,
	(val) => {
		if (val !== null) {
			model.value = val
		}
	}
)
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-checkbox {
	display: inline-flex;
	align-items: center;
	cursor: pointer;
	font-size: inherit;
	position: relative;
	&:hover {
		color: var(--blue-300);
		.pronto-icon {
			color: var(--blue-300) !important;
		}
	}
	&.checked {
		.pronto-icon {
			color: var(--blue-300);
			&.empty {
				display: none;
			}
			&.filled {
				display: inline;
			}
		}
	}
	&.unchecked {
		.pronto-icon {
			color: var(--neutral-500);
			&.empty {
				display: inline;
			}
			&.filled {
				display: none;
			}
		}
	}
	&.unchecked:hover {
		.pronto-icon {
			&.empty {
				display: inline;
			}
			&.filled {
				display: none;
			}
		}
	}
	&.pronto {
		&:hover {
			color: var(--teal-300);
			.pronto-icon {
				color: var(--teal-300) !important;
			}
		}
		&.checked {
			.pronto-icon {
				color: var(--teal-300);
			}
		}
	}
	&.loading {
		.pronto-icon {
			display: none !important;
		}
	}
	&.disabled {
		opacity: 0.8;
		&.checked {
			.pronto-icon {
				color: var(--blue-200);
			}
			&:hover {
				color: var(--blue-200);
				.pronto-icon {
					color: var(--blue-200) !important;
				}
			}
		}
		&.unchecked {
			background-color: var(--neutral-100);
			border-radius: 4px;
			overflow: hidden;
			&:hover {
				color: var(--neutral-500);
				.pronto-icon {
					color: var(--neutral-500) !important;
				}
			}
		}
		> input {
			cursor: not-allowed !important;
		}
	}
	> input {
		cursor: pointer;
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: 1;
		opacity: 0;
	}
	.pronto-checkbox-icon {
		width: 1.75em;
		height: 1em;
		> .pronto-icon {
			font-size: 1em;
		}
	}
	.spinner-div {
		position: static !important;
		margin: 0 !important;
		width: 1em !important;
		height: 1em !important;
		overflow: hidden;
		:deep(div:first-child) {
			display: none !important;
		}
		:deep(.ios-spinner) {
			margin-top: -2px !important;
			width: 1em !important;
			height: 1em !important;
		}
	}
}
</style>
